
import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DetailView } from '@/components/ui/detail-view';
import { LoanRequestForm, LoanFormData } from '@/components/loans/LoanRequestForm';
import { PlusCircle, Search, FileText, Check, X } from 'lucide-react';
import { 
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { formatCurrency, formatDate } from '@/lib/utils';
import { Loan, Employee } from '@/types';
import { useAppDataContext } from '@/contexts/AppDataContext';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/services/api';

const Loans = () => {
  const [isRequestOpen, setIsRequestOpen] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(null);
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const { data, loading, refresh } = useAppDataContext();
  const { toast } = useToast();
  const { user } = useAuth();
  const { employees, loans } = data;

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  // Get current user's employee ID
  const getCurrentUserEmployeeId = () => {
    if (!user || !user.email) return null;
    
    // Find employee by matching email
    const employee = employees.find(emp => emp.email === user.email);
    return employee ? employee.id : null;
  };

  const currentUserEmployeeId = getCurrentUserEmployeeId();

  const handleOpenRequest = () => {
    setIsRequestOpen(true);
  };

  const handleCloseRequest = () => {
    setIsRequestOpen(false);
  };

  const handleViewDetails = (loan: Loan) => {
    setSelectedLoan(loan);
    setIsDetailViewOpen(true);
  };

  const handleCloseDetails = () => {
    setIsDetailViewOpen(false);
  };

  const handleCreateLoan = async (formData: LoanFormData) => {
    try {
      // Transform LoanFormData to Loan type with required fields
      const advanceData: Loan = {
        id: '', // Will be generated by backend
        employeeId: formData.employeeId,
        amount: formData.amount,
        installmentAmount: formData.installmentAmount || 0,
        purpose: formData.purpose,
        notes: formData.notes,
        requestDate: new Date().toISOString().split('T')[0],
        status: 'pending',
        remainingAmount: formData.amount,
        // Add advance-specific fields
        monthsForPayment: formData.monthsForPayment,
        startMonth: formData.startMonth
      };
      
      await apiService.createAdvanceRequest(advanceData);
      toast({
        title: "Advance request submitted",
        description: "Your advance request has been submitted successfully.",
      });
      
      // // Refresh loans data
      refresh.loans();
      return true;
    } catch (err) {
      console.error("Error creating advance:", err);
      toast({
        title: "Error",
        description: "Failed to submit advance request. Please try again.",
        variant: "destructive",
      });
      return false;
    } finally {
      handleCloseRequest();
    }
  };

  const handleApproveLoan = async (loanId: string, employeeId: string) => {
    try {
      await apiService.approveAdvance({
        id: parseInt(loanId),
        approver_id: user?.id ? parseInt(user.id.toString()) : 0
      });
      toast({
        title: "Advance Approved",
        description: "The advance has been approved successfully.",
      });
      
      // Refresh loans data
      refresh.loans();
    } catch (err) {
      console.error("Error approving advance:", err);
      toast({
        title: "Error",
        description: "Failed to approve advance. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleRejectLoan = async (loanId: string, employeeId: string) => {
    try {
      await apiService.rejectAdvance({
        id: parseInt(loanId),
        approver_id: user?.id ? parseInt(user.id.toString()) : 0
      });
      toast({
        title: "Advance Rejected",
        description: "The advance has been rejected.",
      });
      
      // Refresh loans data
      refresh.loans();
    } catch (err) {
      console.error("Error rejecting advance:", err);
      toast({
        title: "Error",
        description: "Failed to reject advance. Please try again.",
        variant: "destructive",
      });
    }
  };

  const filteredLoans = (loans || []).filter(loan => {
    // If user is not admin, only show their own loans
    if (!isAdmin && currentUserEmployeeId) {
      if (loan.employeeId !== currentUserEmployeeId) {
        return false;
      }
    }

    const employee = employees.find(emp => emp.id === loan.employeeId);
    const employeeName = employee ? `${employee.firstName} ${employee.lastName}` : 'Unknown';

    const searchMatch = employeeName.toLowerCase().includes(searchQuery.toLowerCase());
    const statusMatch = filterStatus === 'all' || !filterStatus ? true : loan.status === filterStatus;

    return searchMatch && statusMatch;
  });

  const getEmployeeName = (employeeId: string) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? `${employee.firstName} ${employee.lastName}` : 'Unknown';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Advance Management</h1>
            <p className="text-gray-500">
              {isAdmin ? 'Manage employee advance requests' : 'View and manage your advance requests'}
            </p>
          </div>
          <Button className="bg-payroll-primary hover:bg-blue-700" onClick={handleOpenRequest}>
            <PlusCircle className="h-4 w-4 mr-2" />
            Request Advance
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Advance Requests</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
              {isAdmin && (
                <div className="col-span-2 md:col-span-1">
                  <Label htmlFor="search">Search Employee</Label>
                  <Input
                    id="search"
                    type="search"
                    placeholder="Search by employee name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              )}
              <div>
                <Label htmlFor="status">Filter by Status</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Status</SelectLabel>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-payroll-primary"></div>
              </div>
            ) : filteredLoans.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No advances found
              </div>
            ) : (
              <div className="mt-4 -mx-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {isAdmin && <TableHead>Employee</TableHead>}
                      <TableHead>Amount</TableHead>
                      <TableHead>Monthly Payment</TableHead>
                      <TableHead>Months</TableHead>
                      <TableHead>Request Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLoans.map((loan) => (
                      <TableRow key={loan.id}>
                        {isAdmin && (
                          <TableCell className="font-medium">{getEmployeeName(loan.employeeId)}</TableCell>
                        )}
                        <TableCell>{formatCurrency(loan.amount)}</TableCell>
                        <TableCell>{formatCurrency(loan.installmentAmount)}</TableCell>
                        <TableCell>{loan.monthsForPayment || 'N/A'}</TableCell>
                        <TableCell>{formatDate(loan.requestDate)}</TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(loan.status)}>
                            {loan.status.charAt(0).toUpperCase() + loan.status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => handleViewDetails(loan)}>
                            <FileText className="h-4 w-4 mr-2" />
                            View
                          </Button>
                          {loan.status === 'pending' && isAdmin && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => handleApproveLoan(loan.id, loan.employeeId)}>
                                <Check className="h-4 w-4 mr-2 text-green-500" />
                                Approve
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleRejectLoan(loan.id, loan.employeeId)}>
                                <X className="h-4 w-4 mr-2 text-red-500" />
                                Reject
                              </Button>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <LoanRequestForm 
          isOpen={isRequestOpen} 
          onClose={handleCloseRequest} 
          onSubmit={handleCreateLoan} 
          employees={employees}
          isAdmin={isAdmin}
          currentUserEmployeeId={currentUserEmployeeId}
        />

        {selectedLoan && (
          <DetailView
            title="Advance Details"
            isOpen={isDetailViewOpen}
            onClose={handleCloseDetails}
            size="xl"
          >
            <Card className="w-full">
              <CardHeader>
                <CardTitle>Advance Details</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Employee Name</Label>
                    <p className="font-medium">{getEmployeeName(selectedLoan.employeeId)}</p>
                  </div>
                  <div>
                    <Label>Amount</Label>
                    <p className="font-medium">{formatCurrency(selectedLoan.amount)}</p>
                  </div>
                  <div>
                    <Label>Request Date</Label>
                    <p className="font-medium">{formatDate(selectedLoan.requestDate)}</p>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Badge className={getStatusColor(selectedLoan.status)}>
                      {selectedLoan.status.charAt(0).toUpperCase() + selectedLoan.status.slice(1)}
                    </Badge>
                  </div>
                  <div>
                    <Label>Purpose</Label>
                    <p className="font-medium">{selectedLoan.purpose}</p>
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <p className="font-medium">{selectedLoan.notes || 'No additional notes'}</p>
                  </div>
                  {selectedLoan.monthsForPayment && (
                    <div>
                      <Label>Months for Payment</Label>
                      <p className="font-medium">{selectedLoan.monthsForPayment} months</p>
                    </div>
                  )}
                  {selectedLoan.startMonth && (
                    <div>
                      <Label>Start Month</Label>
                      <p className="font-medium">
                        {new Date(selectedLoan.startMonth + '-01').toLocaleString('default', { 
                          month: 'long', 
                          year: 'numeric' 
                        })}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </DetailView>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Loans;
